import "../../../assets/css/SalesTrend/FilterBar.css";
import React, { useState, useEffect } from "react";
import FilterListIcon from "../../../assets/images/FilterImg.svg";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Button,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { API_BASE_URL } from "../../../config/config";
import { useSalesContext } from "../../SalesContext";

const StyledButton = styled(Button)(({ disabled }) => ({
  margin: "8px 4px 2px 4px",
  padding: "0 1px",
  height: "16px",
  lineHeight: "16px",
  width: "calc(50% - 6px)",
  textAlign: "center",
  fontSize: "12px",
  color: disabled ? "rgba(0, 0, 0, 0.38)" : "rgba(0, 0, 0, 0.8)",
  border: "1px solid transparent",
  borderRadius: "1px",
  backgroundColor: disabled ? "rgba(0, 0, 0, 0.12)" : "rgba(0, 0, 0, 0.18)",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: disabled ? "rgba(0, 0, 0, 0.12)" : "rgba(0, 0, 0, 0.3)",
    boxShadow: "none",
  },
}));

const getCurrentWeek = () => {
  const today = new Date();
  const dayOfMonth = today.getDate();
  return Math.ceil(dayOfMonth / 7);
};

const getAllFilterOptions = (data, selectedMonth) => {
  const divisions = new Set();
  data.filter_Data.forEach((division) => {
    if (division.divisionname && division.divisionname !== "") {
      divisions.add(division.divisionname);
    }
  });

  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currentMonth = new Date().getMonth();
  const months = allMonths.slice(0, currentMonth + 1);
  const selectedMonthIndex = allMonths.indexOf(selectedMonth);
  const isCurrentMonth = selectedMonthIndex === currentMonth;
  const currentWeek = getCurrentWeek();

  let weeks;
  if (isCurrentMonth) {
    weeks = ["Week1", "Week2", "Week3", "Week4"].slice(0, currentWeek);
  } else if (selectedMonthIndex < currentMonth) {
    weeks = ["Week1", "Week2", "Week3", "Week4"];
  } else {
    weeks = ["Week1", "Week2", "Week3", "Week4"];
  }

  return {
    division: Array.from(divisions),
    months,
    weeks,
  };
};

const getCodeMappings = (data) => {
  const divisionMap = {};
  data.filter_Data.forEach((division) => {
    divisionMap[division.divisionname] = division.divisioncode;
  });
  return { divisionMap };
};

const MultiFilterSelect = ({ user_id, apiEndpoint }) => {
  const { setKpiData, setLoading, setuserRole, setuserID } = useSalesContext();
  const [filterData, setFilterData] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [tempFilters, setTempFilters] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [userDetails, setUserDetails] = useState({
    userId: null,
    userRole: null,
  });
  const maxTextLength = 15;

  const currentMonth = new Date().getMonth();
  const monthNames = getAllFilterOptions({ filter_Data: [] }, "").months;
  const defaultMonth = monthNames[currentMonth];
  const defaultWeek = `Week${getCurrentWeek()}`;

  useEffect(() => {
    const userId = user_id || "demouser74@mankind.com";
    fetch(`${API_BASE_URL}Filters/Filters?userId=${userId}&userType=SAP_USER`)
      .then((response) => response.json())
      .then((data) => {
        setuserRole(data?.user_Details?.user_Role);
        setuserID(data?.user_Details?.user_Id);

        setFilterData(data);
        setUserDetails({
          userId: data?.user_Details?.user_Id,
          userRole: data?.user_Details?.user_Role,
        });
        const filterOptions = getAllFilterOptions(data, defaultMonth);
        const initialFilters = {
          division: filterOptions.division,
          months: defaultMonth,
          weeks: defaultWeek,
        };
        setSelectedFilters(initialFilters);
        setTempFilters(initialFilters);
      })
      .catch((error) => {
        console.error("Error fetching filter data:", error);
      });
  }, [user_id]);

  const convertToCodeFormat = (selectedFilters, mappings, userDetails) => {
    const { divisionMap } = mappings;
    return {
      divisionCode:
        selectedFilters?.division.map((name) => divisionMap[name]) || [],
      month: selectedFilters?.months || "",
      week: selectedFilters?.weeks
        ? Number(selectedFilters.weeks.replace("Week", ""))
        : null,
      userId: userDetails?.userId || null,
      userRole: userDetails?.userRole || null,
    };
  };

  useEffect(() => {
    if (
      selectedFilters &&
      filterData &&
      userDetails.userId &&
      userDetails.userRole
    ) {
      const mappings = getCodeMappings(filterData);
      const codeFormat = convertToCodeFormat(
        selectedFilters,
        mappings,
        userDetails
      );

      fetch(`${API_BASE_URL}${apiEndpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(codeFormat),
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          setKpiData(data);
        })
        .catch((error) => {
          console.error("Initial API Error:", error);
        });
    }
  }, [filterData, selectedFilters, userDetails, apiEndpoint]);

  const handleChange = (filter, event) => {
    const value = event.target.value;
    const filterOptions = getAllFilterOptions(filterData, tempFilters.months);

    if (filter === "months" || filter === "weeks") {
      const newValue = value;
      let updatedFilters = { ...tempFilters, [filter]: newValue };

      if (filter === "months") {
        const newFilterOptions = getAllFilterOptions(filterData, newValue);
        updatedFilters.weeks = newFilterOptions.weeks[0] || "";
      }

      setTempFilters(updatedFilters);
      setSelectedFilters((prev) => {
        const updated = { ...prev, ...updatedFilters };
        const prevSelected = prev[filter];
        if (newValue !== prevSelected) {
          const mappings = getCodeMappings(filterData);
          const codeFormat = convertToCodeFormat(
            updated,
            mappings,
            userDetails
          );
          fetch(`${API_BASE_URL}${apiEndpoint}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(codeFormat),
          })
            .then((response) => response.json())
            .then((data) => {
              setLoading(false);
              setKpiData(data);
            })
            .catch((error) => {
              console.error("API Error:", error);
            });
        }
        return updated;
      });
      setOpenDropdown(null);
    } else {
      if (value.includes("all")) {
        const isSelectingAll =
          tempFilters[filter].length !== filterOptions[filter].length;
        setTempFilters((prev) => ({
          ...prev,
          [filter]: isSelectingAll ? [...filterOptions[filter]] : [],
        }));
      } else {
        setTempFilters((prev) => ({
          ...prev,
          [filter]: value,
        }));
      }
    }
  };

  const handleApply = (filter) => {
    const newSelected = tempFilters[filter];
    const mappings = getCodeMappings(filterData);
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev, [filter]: newSelected };
      const prevSelected = prev[filter];
      const hasChanged =
        JSON.stringify(newSelected.sort()) !==
        JSON.stringify(prevSelected.sort());

      if (hasChanged) {
        const codeFormat = convertToCodeFormat(
          updatedFilters,
          mappings,
          userDetails
        );
        fetch(`${API_BASE_URL}${apiEndpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(codeFormat),
        })
          .then((response) => response.json())
          .then((data) => {
            setLoading(false);
            setKpiData(data);
          })
          .catch((error) => {
            console.error("API Error:", error);
          });
      }
      return updatedFilters;
    });

    setTempFilters((prev) => ({
      ...prev,
      [filter]: newSelected,
    }));
    setOpenDropdown(null);
  };

  const handleCancel = (filter) => {
    setTempFilters((prev) => ({ ...prev, [filter]: selectedFilters[filter] }));
    setOpenDropdown(null);
  };

  const handleOpen = (filter) => {
    setTempFilters((prev) => ({ ...prev, [filter]: selectedFilters[filter] }));
    setOpenDropdown(filter);
  };

  const selectStyles = {
    maxWidth: 175,
    height: 22,
    borderRadius: "1px",
    borderWidth: "1px",
    gap: "4px",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "16px",
    textTransform: "uppercase",
    padding: "0",
    "& .MuiSelect-select": {
      width: "100%",
      textTransform: "uppercase",
      textOverflow: "clip",
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: "14px",
      paddingTop: "18px",
    },
    "& .MuiSelect-icon": {
      marginLeft: "20px",
      right: "5px",
    },
  };

  if (
    !filterData ||
    !selectedFilters ||
    !tempFilters ||
    !userDetails.userId ||
    !userDetails.userRole
  ) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const filterOptions = getAllFilterOptions(filterData, tempFilters.months);

  return (
    <div className="filter-container">
      <img src={FilterListIcon} alt="Filter Icon" className="filter-icon" />
      <Grid
        container
        spacing={1}
        sx={{ flexWrap: "nowrap", justifyContent: "right" }}
      >
        {Object.keys(filterOptions).map((filter) => (
          <Grid item xs={2} key={filter}>
            <InputLabel
              sx={{
                textTransform: "capitalize",
                fontWeight: 400,
                fontSize: "14px",
                padding: "3px 3px",
                color: "red",
              }}
            >
              {filter}
            </InputLabel>
            <FormControl fullWidth>
              <Select
                multiple={filter === "division"}
                open={openDropdown === filter}
                onOpen={() => handleOpen(filter)}
                onClose={() => {
                  if (filter === "division") {
                    setTempFilters((prev) => ({
                      ...prev,
                      [filter]: selectedFilters[filter],
                    }));
                  }
                  setOpenDropdown(null);
                }}
                value={tempFilters[filter]}
                onChange={(event) => handleChange(filter, event)}
                renderValue={(selected) => {
                  if (filter === "division") {
                    const availableOptions = filterOptions[filter] || [];
                    if (
                      Array.isArray(selected) &&
                      selected.length === availableOptions.length &&
                      selected.every((item) => availableOptions.includes(item))
                    ) {
                      const text = "ALL selected";
                      return text.length > maxTextLength
                        ? `${text.substring(0, maxTextLength)}..`
                        : text;
                    }
                    const text = selected.join(", ");
                    return text.length > maxTextLength
                      ? `${text.substring(0, maxTextLength)}..`
                      : text;
                  } else {
                    const text = selected || `Select ${filter}`;
                    return text.length > maxTextLength
                      ? `${text.substring(0, maxTextLength)}..`
                      : text;
                  }
                }}
                sx={selectStyles}
                size="large"
              >
                {filter === "division" && (
                  <MenuItem value="all">
                    <Checkbox
                      checked={
                        tempFilters[filter]?.length ===
                        filterOptions[filter]?.length
                      }
                      sx={{
                        transform: "scale(0.8)",
                        padding: "0 !important",
                        "& .MuiSvgIcon-root": { fontSize: "19px" },
                      }}
                    />
                    <ListItemText
                      primary="Select All"
                      primaryTypographyProps={{ fontSize: "12px !important" }}
                      sx={{
                        padding: "0 !important",
                        textTransform: "uppercase",
                      }}
                    />
                  </MenuItem>
                )}
                {filterOptions[filter].map((option) => (
                  <MenuItem key={option} value={option}>
                    {filter === "division" && (
                      <Checkbox
                        checked={tempFilters[filter].includes(option)}
                        sx={{
                          transform: "scale(0.8)",
                          padding: "0 !important",
                          "& .MuiSvgIcon-root": { fontSize: "19px" },
                        }}
                      />
                    )}
                    <ListItemText
                      primary={option}
                      primaryTypographyProps={{ fontSize: "12px !important" }}
                      sx={{
                        padding: "0 !important",
                        textTransform: "uppercase",
                      }}
                    />
                  </MenuItem>
                ))}
                {filter === "division" && (
                  <Box display="flex" justifyContent="space-between" p={0}>
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={() => handleApply(filter)}
                      disabled={tempFilters[filter].length === 0}
                    >
                      Apply
                    </StyledButton>
                    <StyledButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleCancel(filter)}
                    >
                      Cancel
                    </StyledButton>
                  </Box>
                )}
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default MultiFilterSelect;
